import { Fragment, useContext, useRef, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import AuthContext from "../AuthContext";
import toast from "react-hot-toast";
import { privateAxios } from "../utils/axios";

export default function AddProduct({
  addProductModalSetting,
  handlePageUpdate,
}) {
  const authContext = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [showCategoryCreate, setShowCategoryCreate] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [product, setProduct] = useState({
    name: "",
    category: "",
    description: "",
    unit: "Pieces",
  });
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const cancelButtonRef = useRef(null);

  // Input refs for keyboard navigation
  const nameRef = useRef();
  const categoryRef = useRef();
  const descriptionRef = useRef();
  const unitRef = useRef();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await privateAxios.get("/category");
      setCategories(res.data?.data);
    } catch (err) {
      toast.error("Failed to load categories");
    }
  };

  const handleInputChange = (key, value) => {
    setProduct({ ...product, [key]: value });
    // Only clear error if the field has a value
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const validateFields = () => {
    const newErrors = {};
    if (!product.name.trim()) newErrors.name = "Product name is required";
    if (!product.category.trim()) newErrors.category = "Category is required";
    if (!product.unit.trim()) newErrors.unit = "Unit is required";
    // if (!product.description.trim())
    //   newErrors.description = "Description is required";

    // Convert empty warranty duration to 0 before submitting
    // if (product.warranty.duration === "") {
    //   setProduct((prev) => ({
    //     ...prev,
    //     warranty: {
    //       ...prev.warranty,
    //       duration: 0,
    //     },
    //   }));
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleKeyDown = (e, field) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Move to next field if current is valid
      if (field === "name") {
        categoryRef.current.focus();
      } else if (field === "category") {
        unitRef.current.focus();
      } else if (field === "unit") {
        descriptionRef.current.focus();
      } else if (field === "description") {
        if (validateFields()) addProduct();
      }
    }
  };

  const addProduct = async () => {
    if (!validateFields()) {
      toast.error("All fields are required!");
      return;
    }
    setLoading(true);
    try {
      const response = await privateAxios.post("/products/create", product);
      if (response.data.success) {
        toast.success("Product added successfully!");
        handlePageUpdate();
        addProductModalSetting();
      } else {
        toast.error("Error adding product");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };
  const handleCreateCategory = async () => {
    if (!newCategory.trim()) {
      return toast.error("Category name is required");
    }

    try {
      const res = await privateAxios.post("/category/create", {
        name: newCategory,
      });

      toast.success("Category created");

      setCategories((prev) => [...prev, res.data]);

      // Auto select newly created category
      // handleInputChange("category", res.data.name);

      setNewCategory("");
      setShowCategoryCreate(false);
      loadCategories();
    } catch (err) {
      // Axios error handling
      if (err.response) {
        // Server responded with error status
        toast.error(err.response.data?.message || "Category already exists");
      } else if (err.request) {
        // Request sent but no response
        toast.error("Server not responding. Please try again.");
      } else {
        // Something else happened
        toast.error("Something went wrong");
      }
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-xl border border-gray-100">
                {/* Close button */}
                <button
                  type="button"
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                  onClick={() => addProductModalSetting()}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>

                <div className="bg-gradient-to-b from-white to-gray-50 px-6 pt-6 pb-4 sm:p-6">
                  <div className="w-full">
                    <Dialog.Title
                      as="h3"
                      className="text-2xl font-semibold text-gray-900 mb-6 pb-2 border-b border-gray-100"
                    >
                      Add Product
                    </Dialog.Title>
                    <form
                      action="#"
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (validateFields()) addProduct();
                      }}
                    >
                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="name"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Name
                          </label>
                          <input
                            ref={nameRef}
                            type="text"
                            name="name"
                            id="name"
                            value={product.name}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(e, "name")}
                            className={`bg-white border-2 ${
                              errors.name
                                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                            } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                            placeholder="Ex. Monitor"
                            required
                          />
                          {errors.name && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="category"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Category
                          </label>

                          <div className="flex gap-2">
                            <select
                              id="category"
                              name="category"
                              value={product.category}
                              onChange={(e) =>
                                handleInputChange(e.target.name, e.target.value)
                              }
                              className={`bg-white border-2 ${
                                errors.category
                                  ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                              } text-gray-900 text-sm rounded-lg block w-full p-3 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                              required
                            >
                              <option value="">Select category</option>
                              {categories.length &&
                                categories?.map((cat) => (
                                  <option key={cat._id} value={cat.name}>
                                    {cat.name}
                                  </option>
                                ))}
                            </select>

                            <button
                              type="button"
                              onClick={() =>
                                setShowCategoryCreate(!showCategoryCreate)
                              }
                              className="border rounded-lg px-3 hover:bg-gray-100"
                              title="Add category"
                            >
                              <PlusIcon className="w-5 h-5" />
                            </button>
                          </div>

                          {showCategoryCreate && (
                            <div className="mt-2 flex gap-2">
                              <input
                                type="text"
                                value={newCategory}
                                onChange={(e) => setNewCategory(e.target.value)}
                                placeholder="New category name"
                                className="bg-white border-2 border-gray-300 text-sm rounded-lg block w-full p-3 focus:ring-2 focus:ring-blue-200 outline-none"
                              />
                              <button
                                type="button"
                                onClick={handleCreateCategory}
                                className="bg-blue-600 text-white rounded-lg px-4"
                              >
                                Create
                              </button>
                            </div>
                          )}

                          {errors.category && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.category}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="unit"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Unit
                          </label>
                          <select
                            ref={unitRef}
                            id="unit"
                            name="unit"
                            className={`bg-white border-2 ${
                              errors.unit
                                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                            } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                            value={product.unit || ""}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                          >
                            <option value="" disabled>
                              Select unit
                            </option>
                            <option value="Pieces">Pieces</option>
                            <option value="Set">Set</option>
                            <option value="Box">Box</option>
                            <option value="Liter">Liter</option>
                            <option value="Meter">Meter</option>
                            <option value="Kilogram">Kilogram</option>
                            <option value="Pair">Pair</option>
                            <option value="Roll">Roll</option>
                          </select>
                          {errors.unit && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.unit}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="description"
                            className="block mb-2 text-sm font-medium text-gray-700"
                          >
                            Description (Optional)
                          </label>
                          <textarea
                            ref={descriptionRef}
                            id="description"
                            rows="3"
                            name="description"
                            className={`bg-white border-2 ${
                              errors.description
                                ? "border-red-500 focus:border-red-500 focus:ring-red-200"
                                : "border-gray-300 focus:border-blue-500 focus:ring-blue-200"
                            } text-gray-900 text-sm rounded-lg block w-full p-3 placeholder-gray-400 transition-all duration-150 shadow-sm focus:ring-2 outline-none`}
                            placeholder="Ex. High-quality engine oil filter suitable for all types of motor vehicles. Ensures efficient filtration of engine oil."
                            value={product.description}
                            onChange={(e) =>
                              handleInputChange(e.target.name, e.target.value)
                            }
                            onKeyDown={(e) => handleKeyDown(e, "description")}
                          />
                          {errors.description && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse sm:px-6 rounded-b-2xl border-t border-gray-100">
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto transition-all duration-150 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                    onClick={addProduct}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v8z"
                          ></path>
                        </svg>
                        Adding...
                      </span>
                    ) : (
                      "Add Product"
                    )}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto transition-all duration-150"
                    onClick={() => addProductModalSetting()}
                    ref={cancelButtonRef}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
